import { formatPrice, calculateBuyPrice, cn, getValueGlowClass, getStaggerClass, getValueTier, getActivePrices, getDefaultEdition } from '../lib/utils';
import { EditionBadge } from './EditionBadge';
import { CardImage } from './CardImage';
import { Badge } from './ui/badge';
import type { Card, Mode } from '../lib/types';

interface CardGridProps {
  cards: Card[];
  mode: Mode;
  buyPercent: number;
  onCardSelect: (card: Card) => void;
  // Tablet mode props
  selectedCardId?: string;
  isTablet?: boolean;
  // Comparison props (tablet only)
  comparisonMode?: boolean;
  comparisonCards?: Card[];
  onToggleCompare?: (card: Card) => void;
}

export function CardGrid({
  cards,
  mode,
  buyPercent,
  onCardSelect,
  selectedCardId,
  isTablet = false,
  comparisonMode = false,
  comparisonCards = [],
  onToggleCompare,
}: CardGridProps) {
  if (cards.length === 0) {
    return null;
  }

  const getDisplayPrice = (card: Card): string => {
    // Use edition-aware prices for vintage cards
    const edition = getDefaultEdition(card);
    const activePrices = getActivePrices(card, edition);
    const price = activePrices?.nm ?? card.price;
    if (price == null) return '-';

    if (mode === 'buy') {
      return formatPrice(calculateBuyPrice(price, buyPercent));
    }
    return formatPrice(price);
  };

  const getCardPrice = (card: Card): number | undefined => {
    // Use edition-aware prices for vintage cards
    const edition = getDefaultEdition(card);
    const activePrices = getActivePrices(card, edition);
    return activePrices?.nm ?? card.price;
  };

  return (
    <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 md:grid-cols-4">
      {cards.map((card, index) => {
        const price = getCardPrice(card);
        const valueTier = getValueTier(price);
        const glowClass = getValueGlowClass(price);
        const staggerClass = getStaggerClass(index);
        const isSelected = selectedCardId === card.id;
        const isInComparison = comparisonCards.some((c) => c.id === card.id);
        const canAddToComparison = comparisonCards.length < 3;

        return (
          <button
            key={card.id}
            onClick={() => onCardSelect(card)}
            className={cn(
              // Dense grid tile — not routed through the Card primitive:
              // Card's shape (rounded-3xl, 24px padding/gap) is tuned for a
              // spacious content card, not a tight image+label thumbnail.
              // Radius scaled down to rounded-xl to match a small tile.
              'group relative flex flex-col overflow-hidden rounded-xl border bg-card shadow-sm transition-all',
              'hover:shadow-md active:scale-[0.98]',
              'animate-fade-in',
              staggerClass,
              // Selected state (tablet mode)
              isSelected && 'ring-2 ring-primary ring-offset-2 ring-offset-background',
              // High value glow (index.css .glow-high-value/.glow-ultra-value)
              !isSelected && glowClass,
              !isSelected && glowClass ? 'border-tertiary' : 'border-outline hover:border-primary/40'
            )}
          >
            {/* Comparison checkbox (tablet only) */}
            {isTablet && comparisonMode && onToggleCompare && (
              <div
                className="absolute right-1 top-1 z-20"
                onClick={(e) => {
                  e.stopPropagation();
                  if (isInComparison || canAddToComparison) {
                    onToggleCompare(card);
                  }
                }}
              >
                <div
                  className={cn(
                    'flex h-6 w-6 items-center justify-center rounded border-2 transition-all',
                    isInComparison
                      ? 'border-primary bg-primary'
                      : canAddToComparison
                        ? 'border-outline bg-card hover:border-primary'
                        : 'border-outline/50 bg-card/50 cursor-not-allowed'
                  )}
                >
                  {isInComparison && (
                    <svg className="h-4 w-4 text-primary-foreground" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                    </svg>
                  )}
                </div>
              </div>
            )}

            {/* Edition badge (vintage sets) */}
            {card.has_editions && card.available_editions?.[0] && (
              <div className="absolute left-1 top-1 z-10">
                <EditionBadge edition={card.available_editions[0]} />
              </div>
            )}

            {/* High-value badge (move down if comparison checkbox present) */}
            {valueTier !== 'standard' && !(isTablet && comparisonMode) && (
              <div className="absolute right-1 top-1 z-10">
                <Badge
                  variant="tertiary"
                  className={cn(
                    'bg-tertiary px-1.5 py-0.5 text-[10px] text-on-tertiary',
                    valueTier === 'high' && 'opacity-80'
                  )}
                >
                  {valueTier === 'ultra' ? 'Hot' : '$50+'}
                </Badge>
              </div>
            )}
            {valueTier !== 'standard' && isTablet && comparisonMode && (
              <div className="absolute right-1 top-8 z-10">
                <Badge
                  variant="tertiary"
                  className={cn(
                    'bg-tertiary px-1.5 py-0.5 text-[10px] text-on-tertiary',
                    valueTier === 'high' && 'opacity-80'
                  )}
                >
                  {valueTier === 'ultra' ? 'Hot' : '$50+'}
                </Badge>
              </div>
            )}

            {/* Card image */}
            <div className="relative aspect-[3/4] bg-muted">
              {card.image_url ? (
                <CardImage
                  src={card.image_url}
                  alt={card.name}
                  className="h-full w-full object-contain"
                  loading="lazy"
                />
              ) : (
                <div className="flex h-full w-full items-center justify-center text-xs text-muted-foreground">
                  No image
                </div>
              )}
            </div>

            {/* Card info */}
            <div className="flex flex-col gap-1 p-2">
              <p className="truncate text-xs font-medium text-card-foreground">
                {card.name}
              </p>
              <p className="truncate text-[10px] text-muted-foreground">
                {card.set_name} · #{card.card_number}
              </p>
              <div className="mt-1 flex items-center justify-between">
                <span
                  className={cn(
                    'text-sm font-bold',
                    mode === 'buy' ? 'text-tertiary' : 'text-primary'
                  )}
                >
                  {getDisplayPrice(card)}
                </span>
                {card.rarity && (
                  <span className="truncate text-[10px] text-muted-foreground">
                    {card.rarity}
                  </span>
                )}
              </div>
            </div>
          </button>
        );
      })}
    </div>
  );
}
